/**
 * Calendar cells ↔ `YYYY-MM-DD` day strings, for react-day-picker.
 *
 * react-day-picker (no `timeZone` prop, which is how components/ui/calendar.tsx
 * uses it) works in the browser's LOCAL time: it renders one cell per local day
 * and hands `onSelect` a Date at LOCAL midnight. So a Date that talks to it about
 * a cell must be built from, and read back through, LOCAL calendar fields.
 *
 * #736 / #632: two pickers built the cell at UTC midnight and read clicks back
 * with getUTC*. In Asia/Kolkata local midnight on Sep 30 is 2026-09-29T18:30Z,
 * so every admin east of UTC stored the day BEFORE the one they clicked, and
 * every admin west of UTC saw the day before highlighted. Only the Y-M-D is
 * carried here — no instant is derived — so this is safe for wall-clock values
 * that are later interpreted in some OTHER zone (the campaign's).
 *
 * Regression suite: lib/time/__tests__/calendar-cell.spec.ts, re-run in real
 * zones by components/ui/__tests__/date-time-picker-clicked-day.tz.spec.ts.
 */

/** `YYYY-MM-DD` → the Date react-day-picker uses for that cell (local midnight). */
export function dayToCell(day: string): Date | undefined {
  const [y, m, d] = day.split('-').map(Number)
  if (!y || !m || !d) return undefined
  return new Date(y, m - 1, d)
}

/** A cell Date from react-day-picker (local midnight) → `YYYY-MM-DD`. */
export function cellToDay(cell: Date): string {
  const y = cell.getFullYear()
  const m = String(cell.getMonth() + 1).padStart(2, '0')
  const d = String(cell.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * A cell Date (local midnight) → the ISO instant for the START of that local
 * day (00:00:00.000). Used as the inclusive LOWER bound of a date-range filter.
 * A react-day-picker cell is already at local midnight, so this is a no-op for
 * one, but it makes the intent explicit and is correct for any Date.
 */
export function cellToStartOfDayIso(cell: Date): string {
  return new Date(
    cell.getFullYear(),
    cell.getMonth(),
    cell.getDate(),
    0,
    0,
    0,
    0,
  ).toISOString()
}

/**
 * A cell Date (local midnight) → the ISO instant for the END of that local day
 * (23:59:59.999). Used as the inclusive UPPER bound of a date-range filter.
 *
 * #F11: react-day-picker hands a clicked day back at LOCAL MIDNIGHT, and the
 * recipient date filters sent that straight through as the range end — so an
 * inclusive `lte(col, end)` excluded everything on the clicked day after 00:00:
 * a single-day pick returned ~0 rows and a multi-day pick dropped its last day.
 * Snapping the upper bound to end-of-day covers the whole clicked day.
 */
export function cellToEndOfDayIso(cell: Date): string {
  return new Date(
    cell.getFullYear(),
    cell.getMonth(),
    cell.getDate(),
    23,
    59,
    59,
    999,
  ).toISOString()
}
